import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useNavigateToPlan } from "hooks/navigation";
import { useFormatLocalDateString } from "hooks/useFormatLocalDate";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import {
  DeployedPlanDaysRead,
  DeployedPlanRead,
  generatedApi,
  useGetDeployedPlansQuery,
} from "src/store/api/generatedApi";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { UnitSuffix } from "../plan/table/common/UnitSuffix";
import { useCanEditAndSendDeploy } from "hooks/useCanEditAndSendDeploy";
import { useTenant } from "hooks/settings";
import theme from "src/theme";
import React from "react";
import { UserLabel } from "src/components/common/userLabel/UserLabel";
import { userDisplayName } from "src/utils/userDisplay";
import { PlanAncestorsTooltip } from "components/common/planHistory/PlanAncestorsTooltip";
import { HighlightedText } from "src/components/common/HighlightedText";
import { useCanSeeRecipeCodes } from "hooks/useCanSeeRecipeCodes";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { RecipeCodesCell } from "./RecipeCodesCell";
import { DeployedOnCell } from "./DeployedOnCell";
import {
  deployedPlanDaysRequestReducer,
  DeployedPlanGridRow,
  flattenDeployedPlanDays,
  hasDayCounts,
  openDaysReducer,
  parseMatchInput,
  viewerTimezone,
} from "./deployedPlanDays";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const TYPING_DELAY_MILLISECONDS = 350;
const REQUEST_DELAY_MILLISECONDS = 200;
const DEPLOYED_ON_COLUMN_WIDTH = 212;
const DATE_COLUMN_MIN_WIDTH = 90;
const TRUNCATED_TEXT_SX = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

interface ShownDeployedPlanDays {
  days: DeployedPlanDaysRead;
  matchText: string | undefined;
  matchIsDate: boolean;
}

export const DeployedPlansTable = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const canSeeRecipeCodes = useCanSeeRecipeCodes();
  const formatLocalDate = useFormatLocalDateString("datetime");
  const { format: formatCount } = useNumberSerialiser();
  const timezone = React.useMemo(viewerTimezone, []);
  const [typedText, setTypedText] = React.useState("");
  const [request, dispatchRequest] = React.useReducer(
    deployedPlanDaysRequestReducer,
    { committedText: "", page: 0, pageSize: 25 }
  );
  const [openDays, dispatchOpenDays] = React.useReducer(openDaysReducer, {
    defaultOpen: "none",
    toggled: new Set<string>(),
  });
  const [listDeployedPlanDaysTrigger, listDeployedPlanDaysResult] =
    generatedApi.endpoints.listDeployedPlanDays.useLazyQuery();

  const debouncedText = useDebouncedValue(
    typedText.trim(),
    TYPING_DELAY_MILLISECONDS
  );
  React.useEffect(() => {
    dispatchRequest({ type: "commitText", text: debouncedText });
  }, [debouncedText]);

  const { matchText, matchDate } = parseMatchInput(request.committedText);
  const hasRequestedRef = React.useRef(false);
  React.useEffect(() => {
    const timer = setTimeout(
      () => {
        hasRequestedRef.current = true;
        void listDeployedPlanDaysTrigger({
          tenantName,
          timezone,
          limit: request.pageSize,
          offset: request.pageSize * request.page,
          matchText: matchText ?? null,
          matchDate: matchDate ?? null,
        });
      },
      hasRequestedRef.current ? REQUEST_DELAY_MILLISECONDS : 0
    );
    return () => {
      clearTimeout(timer);
    };
  }, [
    listDeployedPlanDaysTrigger,
    tenantName,
    timezone,
    request.page,
    request.pageSize,
    matchText,
    matchDate,
  ]);

  const { currentData, originalArgs, isError, isFetching, isUninitialized } =
    listDeployedPlanDaysResult;
  const [shown, setShown] = React.useState<ShownDeployedPlanDays>();
  if (currentData !== undefined && currentData !== shown?.days) {
    const shownMatchText = originalArgs?.matchText ?? undefined;
    if (shownMatchText !== shown?.matchText) {
      dispatchOpenDays({
        type: shownMatchText === undefined ? "collapseAll" : "expandAll",
      });
    }
    setShown({
      days: currentData,
      matchText: shownMatchText,
      matchIsDate: Boolean(originalArgs?.matchDate),
    });
  }
  const loadFailed =
    isError || (shown !== undefined && !hasDayCounts(shown.days));
  const shownDays = loadFailed ? undefined : shown?.days;
  const highlight = shownDays === undefined ? undefined : shown?.matchText;

  const rows = React.useMemo(
    () => flattenDeployedPlanDays({ days: shownDays?.items ?? [], openDays }),
    [shownDays, openDays]
  );
  const canOpenAnyDay = rows.some(({ day }) => (day?.listedPlanCount ?? 0) > 1);
  const paginationModel = React.useMemo<GridPaginationModel>(
    () => ({ page: request.page, pageSize: request.pageSize }),
    [request.page, request.pageSize]
  );

  const renderDate = ({
    value,
  }: GridRenderCellParams<DeployedPlanGridRow, string>) => {
    const text = value === undefined ? "" : formatLocalDate(value);
    return (
      <Typography variant="inherit" noWrap title={text}>
        <HighlightedText
          text={text}
          query={shown?.matchIsDate ? highlight : undefined}
        />
      </Typography>
    );
  };

  const columns: GridColDef<DeployedPlanGridRow>[] = [
    {
      field: "deployed_on",
      headerName: t("deployedOn"),
      width: DEPLOYED_ON_COLUMN_WIDTH,
      display: "flex",
      renderCell: ({
        row,
        tabIndex,
      }: GridRenderCellParams<DeployedPlanGridRow>) => (
        <DeployedOnCell
          row={row}
          isFiltered={highlight !== undefined}
          tabIndex={tabIndex}
          onToggle={(deployedOn) => {
            dispatchOpenDays({ type: "toggle", deployedOn });
          }}
        />
      ),
    },
    {
      field: "name",
      headerName: t("searchName"),
      flex: 2,
      minWidth: 111,
      renderCell: ({
        row,
        tabIndex,
      }: GridRenderCellParams<DeployedPlanGridRow>) => (
        <PlanNameCell plan={row} highlight={highlight} tabIndex={tabIndex} />
      ),
    },
    ...(canSeeRecipeCodes
      ? [
          {
            field: "recipe_codes",
            headerName: t("chargeSchedules"),
            flex: 1.5,
            minWidth: 125,
            display: "flex" as const,
            renderCell: ({
              row,
            }: GridRenderCellParams<DeployedPlanGridRow>) => (
              <RecipeCodesCell
                recipeCodes={row.recipe_codes}
                highlight={highlight}
              />
            ),
          },
        ]
      : []),
    {
      field: "numHeats",
      headerName: t("heats"),
      flex: 0.6,
      minWidth: 60,
      renderCell: NumHeatsCell,
      align: "right",
      display: "flex",
    },
    {
      field: "tappedPrice",
      headerName: t("tappedPrice"),
      flex: 1,
      minWidth: 100,
      renderCell: TappedPriceCell,
      align: "right",
      display: "flex",
    },
    {
      field: "deployed_at",
      headerName: t("deployedAt"),
      flex: 1,
      minWidth: DATE_COLUMN_MIN_WIDTH,
      display: "flex",
      renderCell: renderDate,
    },
    {
      field: "start_at",
      headerName: t("planStart"),
      flex: 1,
      minWidth: DATE_COLUMN_MIN_WIDTH,
      display: "flex",
      renderCell: renderDate,
    },
    {
      field: "end_at",
      headerName: t("planEnd"),
      flex: 1,
      minWidth: DATE_COLUMN_MIN_WIDTH,
      display: "flex",
      renderCell: renderDate,
    },
    {
      field: "deployed_by",
      headerName: t("deployedBy"),
      flex: 1,
      minWidth: 80,
      display: "flex",
      valueGetter: (_value, row) => userDisplayName(row.deployed_by),
      renderCell: ({ row }: GridRenderCellParams<DeployedPlanGridRow>) => (
        <UserLabel
          user={row.deployed_by}
          highlight={highlight}
          sx={TRUNCATED_TEXT_SX}
        />
      ),
    },
  ];

  if (shownDays?.total === 0 && highlight === undefined) {
    return (
      <Box
        sx={{
          borderRadius: 0.5,
          px: 2,
          py: 1,
          backgroundColor: "grey.100",
          display: "inline-block",
          inlineSize: "fit-content",
        }}
      >
        <Typography sx={{ color: theme.palette.text.secondary }}>
          {t("noDeployedPlans")}
        </Typography>
      </Box>
    );
  }

  const countLabel =
    shownDays === undefined || shownDays.total === 0
      ? ""
      : t(highlight === undefined ? "deployedPlanCount" : "matchingPlanCount", {
          plans: formatCount(shownDays.plan_total),
          days: formatCount(shownDays.total),
        });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", minWidth: 200 }}
          >
            {countLabel}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            disabled={!canOpenAnyDay}
            onClick={() => {
              dispatchOpenDays({ type: "expandAll" });
            }}
          >
            {t("expandAll")}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!canOpenAnyDay}
            onClick={() => {
              dispatchOpenDays({ type: "collapseAll" });
            }}
          >
            {t("collapseAll")}
          </Button>
        </Box>
        <TextField
          size="small"
          type="search"
          value={typedText}
          onChange={(event) => {
            setTypedText(event.target.value);
          }}
          placeholder={
            canSeeRecipeCodes
              ? t("findDeployedPlans")
              : t("findDeployedPlansNoCodes")
          }
          sx={{ width: 320, maxWidth: "100%" }}
        />
      </Box>
      <HotkeyCompliantDataGrid
        sx={{
          flex: 1,
          minHeight: 400,
          "& .deployed-plan-earlier": {
            backgroundColor: ({ palette }) => alpha(palette.grey[50], 0.5),
          },
        }}
        rows={rows}
        rowCount={shownDays?.total ?? 0}
        columns={columns}
        pagination
        loading={isUninitialized || isFetching}
        getRowId={(row) => row.plan_id}
        getRowClassName={({ row }) =>
          row.day === null ? "deployed-plan-earlier" : ""
        }
        hideFooter={false}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        paginationModel={paginationModel}
        onPaginationModelChange={({ page, pageSize }) => {
          dispatchRequest({ type: "setPagination", page, pageSize });
        }}
        paginationMode="server"
        localeText={{
          paginationRowsPerPage: t("daysPerPage"),
          ...(loadFailed
            ? { noRowsLabel: t("deployedPlansLoadError") }
            : highlight === undefined
              ? {}
              : { noRowsLabel: t("nothingMatches", { query: highlight }) }),
        }}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnSorting
        disableRowGrouping
        disableAggregation
      />
    </Box>
  );
};

interface BackupChipProps {
  planId: number;
}

const BackupChip = ({ planId }: BackupChipProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const deployedBackupPlans = useGetDeployedPlansQuery({
    tenantName,
    deploymentTypes: ["backup"],
  });
  if (deployedBackupPlans.data?.latest === planId) {
    return (
      <Chip
        label={t("backup")}
        variant="outlined"
        size="small"
        color="info"
        sx={{ justifySelf: "start" }}
      />
    );
  } else {
    return null;
  }
};

const PlanNameCell = ({
  plan: { plan_id, session_name, search_id },
  highlight,
  tabIndex,
}: {
  plan: DeployedPlanRead;
  highlight: string | undefined;
  tabIndex: 0 | -1;
}) => {
  const { t } = useTenantTranslation();
  const navigateToPlan = useNavigateToPlan();
  const canEditAndSendDeploy = useCanEditAndSendDeploy();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minWidth: 0,
        gap: 1,
      }}
    >
      <Tooltip title={t("openPlan")}>
        <Link
          component="button"
          variant="button"
          underline="hover"
          tabIndex={tabIndex}
          onClick={() => {
            navigateToPlan(plan_id, search_id);
          }}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          <HighlightedText text={session_name} query={highlight} />
        </Link>
      </Tooltip>
      {canEditAndSendDeploy ? <BackupChip planId={plan_id} /> : null}
      <Tooltip
        title={<PlanAncestorsTooltip planId={plan_id} />}
        placement="right"
        arrow
        enterDelay={100}
        leaveDelay={500}
        slotProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                bgcolor: "background.paper",
                color: "text.primary",
                p: 0,
                maxWidth: "none",
              },
            },
          },
        }}
      >
        <IconButton
          size="small"
          tabIndex={tabIndex}
          sx={{ marginLeft: "auto" }}
        >
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const NumHeatsCell = ({
  row: { first_period_summary },
}: GridRenderCellParams<DeployedPlanGridRow>) => {
  const { format } = useNumberSerialiser();

  return (
    <Typography variant="body1Mono" textAlign="right">
      {format(first_period_summary.num_heats)}
    </Typography>
  );
};

const TappedPriceCell = ({
  row: { first_period_summary },
}: GridRenderCellParams<DeployedPlanGridRow>) => {
  const units = useUnitsFormatter(false);
  const { format } = useNumberSerialiser({ decimalPlaces: 2 });

  return (
    <Typography variant="body1Mono" sx={{ textAlign: "right" }}>
      {format(first_period_summary.display_cost_per_tonne)}
      <UnitSuffix>{units("specific_cost")}</UnitSuffix>
    </Typography>
  );
};
